import React, { useMemo, useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';

import { AssetLoader } from '../../atoms';
import { AssetChartChat, AgenticAssetChat, Asset } from '../../organisms';
import { ConversationHeaderDropdown } from '../../molecules';
import { Header, NewButton, Icon, ViewOnlyButton } from '../../../../../components';
import { useAgenticAssetBuilder, useSpaces } from '../../../providers';
import { useCopyLink } from '../../../../../hooks';

const AssetBuilderContainer = styled.div`
  display: flex;
  height: calc(100% - 77px); // 77px is the height of the header
  overflow: hidden;
`;

const QuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  width: ${(props) => (props.collapsed ? '100%' : '404px')};
  justify-content: flex-start;
  border-right: 1px solid var(--border-muted);
  transition: width 250ms ease;
`;

const AnswerContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  width: ${(props) => (props.collapsed ? '0' : 'calc(100% - 404px)')};
  box-sizing: border-box;
  transition: width 250ms ease;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const AgenticAssetBuilderLayout = () => {
  const {
    selectedQuestion,
    currentConversation,
    handleCreateQuestion,
    setIsChartMode,
    setSelectedQuestion,
    isChartMode,
    questions,
    isFetchingQuestions,
    termTranslations,
    sources,
    handleUpdateAssetTitle,
    handleUpdateAnswer,
    handleAddQuestionToSpace,
    handleRemoveQuestionFromSpace,
    handleUpdateConversation,
    handleShare,
    handleDeleteQuestion,
    handleRetryQuestion,
    currentUser,
    handleCreateAnswer,
    users,
    findEntityMatches,
    matchingEntities,
    handleSelectEntity
  } = useAgenticAssetBuilder();

  const [isAssetCollapsed, setIsAssetCollapsed] = useState(true);
  const [activeChildQuestion, setActiveChildQuestion] = useState(null);

  const [isEditSqlError, setIsEditSqlError] = useState(false);

  const [activeView, setActiveView] = useState('table');

  useEffect(() => {
    if (!selectedQuestion?.isClarify && selectedQuestion?.answer) {
      setIsAssetCollapsed(false);
    } else {
      setIsAssetCollapsed(true);
    }
  }, [selectedQuestion?.isClarify, selectedQuestion?.answer, selectedQuestion?.id]);

  useEffect(() => {
    setActiveChildQuestion(null);
  }, [currentConversation?.id]);

  useEffect(() => {
    setIsEditSqlError(false);
  }, [selectedQuestion?.id]);

  const { spaces, createSpace } = useSpaces();
  const copyLink = useCopyLink();

  const currentQuestion = useMemo(() => {
    if (selectedQuestion) {
      return selectedQuestion;
    }

    return currentConversation?.assetQuestion;
  }, [selectedQuestion, selectedQuestion?.answer?.id, currentConversation?.assetQuestion]);

  const handleViewChange = useCallback(
    (view) => {
      // 1) Update your current view
      setActiveView(view);

      // 2) Turn chart mode on only when view === 'chart'
      if (view === 'chart') {
        setActiveChildQuestion(null);
      }
      setIsChartMode(view === 'chart');
    },
    [setActiveView, setIsChartMode, currentQuestion]
  );

  const switchToChartMode = useCallback(() => {
    setActiveChildQuestion(null);
    handleViewChange('chart');
    setIsAssetCollapsed(false);
  }, [handleViewChange]);

  const handleUpdateTitle = useCallback(
    (chatTitle) => {
      handleUpdateConversation({ title: chatTitle });
    },
    [handleUpdateConversation]
  );

  const handleCopyLink = useCallback(() => {
    if (currentConversation?.id && currentConversation?.organizationId) {
      copyLink(
        `${window.location.origin}/agentic-chats/${currentConversation.id}?organization_id=${currentConversation.organizationId}`
      );
    }
  }, [currentConversation, copyLink]);

  const handleCollapseAsset = useCallback(() => {
    setActiveChildQuestion(null);
    setIsAssetCollapsed(false);
    setSelectedQuestion(null);
  }, []);

  const shouldShowLoader = isFetchingQuestions;

  if (shouldShowLoader) {
    return <AssetLoader title='Loading the conversation' subtitle='Getting all answers...' />;
  }

  return (
    <>
      <Header
        title={currentConversation?.title || 'Agentic Chat'}
        onUpdateTitle={handleUpdateTitle}
        actions={
          currentUser?.canAsk ? (
            <>
              <NewButton tertiary onClick={handleShare}>
                <Icon name='user-plus' />
              </NewButton>
              <ConversationHeaderDropdown onCopyLinkClick={handleCopyLink} />
            </>
          ) : (
            <ViewOnlyButton />
          )
        }
        isReadOnly={!currentUser?.canAsk}
      />

      <AssetBuilderContainer>
        <QuestionsContainer collapsed={isAssetCollapsed}>
          {isChartMode ? (
            <AssetChartChat
              currentQuestion={currentQuestion}
              handleCreateQuestion={handleCreateQuestion}
              handleRetryQuestion={handleRetryQuestion}
              handleDeleteQuestion={handleDeleteQuestion}
              isReadOnly={!currentUser?.canAsk}
              questions={questions}
              users={users}
              sources={sources}
              handleSelectQuestion={setSelectedQuestion}
              switchToTableMode={() => handleViewChange('table')}
              currentUser={currentUser}
              findEntityMatches={findEntityMatches}
              matchingEntities={matchingEntities}
              handleSelectEntity={handleSelectEntity}
            />
          ) : (
            <AgenticAssetChat
              assetQuestion={currentConversation?.assetQuestion}
              isReadOnly={!currentUser?.canAsk}
              setIsAssetCollapsed={setIsAssetCollapsed}
              switchToChartMode={switchToChartMode}
              handleViewChange={handleViewChange}
              setActiveChildQuestion={setActiveChildQuestion}
              activeChildQuestion={activeChildQuestion}
            />
          )}
        </QuestionsContainer>

        <AnswerContainer collapsed={isAssetCollapsed}>
          {currentQuestion?.answer && (
            <Asset
              handleCollapseAsset={handleCollapseAsset}
              question={currentQuestion}
              updateAssetTitle={handleUpdateAssetTitle}
              updateAnswer={handleUpdateAnswer}
              createSpace={createSpace}
              removeQuestionFromSpace={handleRemoveQuestionFromSpace}
              addQuestionToSpace={handleAddQuestionToSpace}
              termTranslations={termTranslations}
              sources={sources}
              spaces={spaces}
              handleCreateQuestion={handleCreateQuestion}
              handleDeleteQuestion={handleDeleteQuestion}
              handleRetryQuestion={handleRetryQuestion}
              isReadOnly={!currentUser?.canAsk}
              handleCreateAnswer={handleCreateAnswer}
              activeView={activeView}
              handleViewChange={handleViewChange}
              setActiveChildQuestion={setActiveChildQuestion}
              activeChildQuestion={activeChildQuestion}
              isEditSqlError={isEditSqlError}
              setIsEditSqlError={setIsEditSqlError}
            />
          )}
        </AnswerContainer>
      </AssetBuilderContainer>
    </>
  );
};

export { AgenticAssetBuilderLayout };
